import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  TextField,
  Stepper,
  Step,
  StepButton,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Tabs,
  Tab,
  LinearProgress,
} from "@mui/material";
import {
  Close,
  Add,
  Delete,
  CheckCircle,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";
import {
  useMedicalRecordService,
  GeneratedDocument,
} from "../../../../hooks/api/medicalRecord";
import { OptionsResponse } from "../../../../hooks/api/medicalRecord/interfaces/options.response";
import { useAppointmentService } from "../../../../hooks/api/Appointment";
import { useAlert } from "../../../../hooks/alert";
import debounce from "lodash/debounce";

interface MedicalRecordModalProps {
  open: boolean;
  onClose: () => void;
  appointmentId?: number;
}

interface MedicineWithDosage {
  medicine: OptionsResponse;
  dosage: string;
}

const STEP_LABELS = [
  "Queixa Principal",
  "Histórico da Doença",
  "Comorbidades",
  "Medicamentos em Uso",
  "Exame Clínico",
  "Hipótese Diagnóstica",
  "Conduta Médica",
  "Exames de Imagem",
  "Exames Laboratoriais",
  "Prescrever Medicamento",
  "CID",
  "Atestado Médico",
  "Resumo Final",
  "Assinatura",
  "Documentos",
];

const PRIMARY_STEP_COLOR = "#0A6172";
const HIDE_CENTER_STEP_LABELS = new Set([0, 1, 3, 4, 5, 6, 7, 8, 12]);

const parseExamFields = (value?: string) => {
  const fields = value?.split(/\r?\n/).filter((field) => field.trim()) ?? [];
  return fields.length > 0 ? fields : [""];
};

export const MedicalRecordModal: React.FC<MedicalRecordModalProps> = ({
  open,
  onClose,
  appointmentId: appointmentIdProp,
}) => {
  const { id } = useParams();
  const appointmentId = appointmentIdProp ?? Number(id);
  const { success, error: errorAlert } = useAlert();

  const {
    postCreateMedicalRecord,
    getRecordByAppointment,
    searchComorbidities,
    getMedicinePaginated,
    generatePdfs,
    startSignature,
    createMedicine,
    createComorbidity,
  } = useMedicalRecordService();

  const { getAppointment } = useAppointmentService();
  const getAppointmentRef = useRef(getAppointment);
  getAppointmentRef.current = getAppointment;

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [medicalRecordId, setMedicalRecordId] = useState<number | null>(null);

  // Form data
  const [complaint, setComplaint] = useState("");
  const [history, setHistory] = useState("");
  const [currentMedicines, setCurrentMedicines] = useState("");
  const [exams, setExams] = useState("");
  const [hypothesis, setHypothesis] = useState("");
  const [conduct, setConduct] = useState("");
  const [examsRequested, setExamsRequested] = useState<string[]>([""]);
  const [laboratoryExamsRequested, setLaboratoryExamsRequested] = useState<
    string[]
  >([""]);
  const [cid, setCid] = useState("");
  const [healthCertificate, setHealthCertificate] = useState("");
  const [report, setReport] = useState("");

  // Comorbidities
  const [selectedComorbidities, setSelectedComorbidities] = useState<
    OptionsResponse[]
  >([]);
  const [comorbiditySearch, setComorbiditySearch] = useState("");
  const [comorbidityOptions, setComorbidityOptions] = useState<
    OptionsResponse[]
  >([]);
  const [loadingComorbidities, setLoadingComorbidities] = useState(false);

  // Medicines
  const [selectedMedicines, setSelectedMedicines] = useState<
    MedicineWithDosage[]
  >([]);
  const [medicineSearch, setMedicineSearch] = useState("");
  const [medicineOptions, setMedicineOptions] = useState<OptionsResponse[]>([]);
  const [loadingMedicines, setLoadingMedicines] = useState(false);
  const [medicinePage, setMedicinePage] = useState(1);
  const [hasMoreMedicines, setHasMoreMedicines] = useState(true);
  const [dosageModalOpen, setDosageModalOpen] = useState(false);
  const [currentMedicine, setCurrentMedicine] =
    useState<OptionsResponse | null>(null);
  const [currentDosage, setCurrentDosage] = useState("");


  // PDF Documents
  const [generatedDocs, setGeneratedDocs] = useState<GeneratedDocument[]>([]);
  const [selectedDocTab, setSelectedDocTab] = useState(0);
  const [generatingPdfs, setGeneratingPdfs] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const docTabRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Appointment info
  const [patientName, setPatientName] = useState("");
  const [appointmentStartTime, setAppointmentStartTime] = useState<string>("");

  // Signature
  const [signatureComplete, setSignatureComplete] = useState(false);

  const medicineListRef = useRef<HTMLDivElement>(null);
  const medicineSearchRef = useRef<HTMLInputElement>(null);
  const stepButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    if (!window.matchMedia("(max-width: 600px)").matches) return;

    docTabRefs.current[selectedDocTab]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "start",
    });
  }, [open, selectedDocTab]);

  const getVideoCallStartedAt = useCallback(
    () =>
      sessionStorage.getItem(`@vivaseVideoCallStartedAt_${appointmentId}`) || "",
    [appointmentId]
  );

  const loadExistingRecord = useCallback(async () => {
    setLoading(true);
    try {
      const record = await getRecordByAppointment(appointmentId);
      if (record) {
        setMedicalRecordId(record.id);
        setComplaint(record.complaint || "");
        setHistory(record.history || "");
        setCurrentMedicines(record.currentMedicines || "");
        setExams(record.exams || "");
        setHypothesis(record.hypothesis || "");
        setConduct(record.conduct || "");
        setExamsRequested(parseExamFields(record.examsRequested));
        setLaboratoryExamsRequested(
          parseExamFields(record.laboratoryExamsRequested)
        );
        setCid(record.cid || "");
        setHealthCertificate(record.healthCertificate || "");
        setReport(record.report || "");
        setPatientName(record.appointment?.patient?.user?.name || "");
        setAppointmentStartTime(
          getVideoCallStartedAt() ||
          record.appointment?.appointment_started_at?.toString() ||
          ""
        );

        if (record.comorbidities) {
          setSelectedComorbidities(record.comorbidities);
        }
        if (record.recordMedicine) {
          setSelectedMedicines(
            record.recordMedicine.map((rm: any) => ({
              medicine: rm.medicine,
              dosage: rm.dosage,
            }))
          );
        }
      } else {
        // No existing record, fetch appointment data for patient name and time
        try {
          const appointment = await getAppointmentRef.current(appointmentId);
          if (appointment) {
            setPatientName(appointment.patient?.user?.name || "");
            setAppointmentStartTime(
              getVideoCallStartedAt() ||
              appointment.appointment_started_at?.toString() ||
              ""
            );
          }
        } catch (e) {
          console.log("Could not fetch appointment data");
        }
      }
    } catch (err) {
      console.log("No existing record");
      // Try to fetch appointment data anyway
      try {
        const appointment = await getAppointmentRef.current(appointmentId);
        if (appointment) {
          setPatientName(appointment.patient?.user?.name || "");
          setAppointmentStartTime(
            getVideoCallStartedAt() ||
            appointment.appointment_started_at?.toString() ||
            ""
          );
        }
      } catch (e) {
        console.log("Could not fetch appointment data");
      }
    }
    setLoading(false);
  }, [appointmentId, getRecordByAppointment, getVideoCallStartedAt]);

  useEffect(() => {
    if (open && appointmentId) {
      loadExistingRecord();
    }
  }, [open, appointmentId, loadExistingRecord]);

  const debouncedComorbiditySearch = useMemo(
    () =>
      debounce(async (term: string) => {
      if (!term || term.length < 2) {
        setComorbidityOptions([]);
        return;
      }
      setLoadingComorbidities(true);
      try {
        const results = await searchComorbidities(term);
        setComorbidityOptions(results);
      } catch (err) {
        console.error(err);
      }
      setLoadingComorbidities(false);
    }, 300),
    [searchComorbidities]
  );

  const debouncedMedicineSearch = useMemo(
    () =>
      debounce(async (term: string, page: number = 1) => {
      setLoadingMedicines(true);
      try {
        const results = await getMedicinePaginated(page, 5, term);
        if (page === 1) {
          setMedicineOptions(results.data);
        } else {
          setMedicineOptions((prev) => [...prev, ...results.data]);
        }
        setHasMoreMedicines(results.data.length === 5);
        setMedicinePage(page);
      } catch (err) {
        console.error(err);
      }
      setLoadingMedicines(false);
    }, 300),
    [getMedicinePaginated]
  );


  const handleComorbiditySearchChange = (value: string) => {
    setComorbiditySearch(value);
    debouncedComorbiditySearch(value);
  };

  const handleMedicineSearchChange = (value: string) => {
    setMedicineSearch(value);
    setMedicinePage(1);
    debouncedMedicineSearch(value, 1);
  };

  const loadMoreMedicines = () => {
    if (!loadingMedicines && hasMoreMedicines) {
      debouncedMedicineSearch(medicineSearch, medicinePage + 1);
    }
  };

  const handleMedicineScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      loadMoreMedicines();
    }
  };

  const addComorbidity = (comorbidity: OptionsResponse) => {
    if (!selectedComorbidities.find((c) => c.id === comorbidity.id)) {
      setSelectedComorbidities([...selectedComorbidities, comorbidity]);
    }
    setComorbiditySearch("");
    setComorbidityOptions([]);
  };

  const removeComorbidity = (id: number) => {
    setSelectedComorbidities(selectedComorbidities.filter((c) => c.id !== id));
  };

  const openDosageModal = (medicine: OptionsResponse) => {
    setCurrentMedicine(medicine);
    setCurrentDosage("");
    setDosageModalOpen(true);
  };

  const saveMedicineWithDosage = () => {
    if (currentMedicine && currentDosage) {
      if (!selectedMedicines.find((m) => m.medicine.id === currentMedicine.id)) {
        setSelectedMedicines([
          ...selectedMedicines,
          { medicine: currentMedicine, dosage: currentDosage },
        ]);
      }
      setDosageModalOpen(false);
      setCurrentMedicine(null);
      setCurrentDosage("");
      // Clear search and focus on input for next medicine
      setMedicineSearch("");
      setMedicineOptions([]);
      setTimeout(() => {
        medicineSearchRef.current?.focus();
      }, 100);
    }
  };

  const removeMedicine = (id: number) => {
    setSelectedMedicines(selectedMedicines.filter((m) => m.medicine.id !== id));
  };

  const addExamField = (type: "clinical" | "laboratory") => {
    if (type === "clinical") {
      setExamsRequested([...examsRequested, ""]);
    } else {
      setLaboratoryExamsRequested([...laboratoryExamsRequested, ""]);
    }
  };

  const updateExamField = (
    type: "clinical" | "laboratory",
    index: number,
    value: string
  ) => {
    if (type === "clinical") {
      const updated = [...examsRequested];
      updated[index] = value;
      setExamsRequested(updated);
    } else {
      const updated = [...laboratoryExamsRequested];
      updated[index] = value;
      setLaboratoryExamsRequested(updated);
    }
  };

  const removeExamField = (type: "clinical" | "laboratory", index: number) => {
    if (type === "clinical") {
      setExamsRequested(examsRequested.filter((_, i) => i !== index));
    } else {
      setLaboratoryExamsRequested(
        laboratoryExamsRequested.filter((_, i) => i !== index)
      );
    }
  };


  const saveRecord = async () => {
    setSaving(true);
    try {
      const body = {
        id: medicalRecordId || undefined,
        appointmentId,
        complaint,
        history,
        currentMedicines,
        exams,
        hypothesis,
        conduct,
        examsRequested: examsRequested.filter((e) => e.trim()).join("\n"),
        laboratoryExamsRequested: laboratoryExamsRequested
          .filter((e) => e.trim())
          .join("\n"),
        cid,
        healthCertificate,
        report,
        comorbidities: selectedComorbidities.map((c) => c.id),
        medicineBody: selectedMedicines.map((m) => ({
          medicine: m.medicine.id,
          dosage: m.dosage,
        })),
      };

      const result = await postCreateMedicalRecord({ body });
      if (result.data?.id) {
        setMedicalRecordId(result.data.id);
      }
      success("Prontuário salvo com sucesso!");
      return true;
    } catch (err) {
      errorAlert("Erro ao salvar prontuário");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (activeStep === 12) {
      // After Report step, generate PDFs with progress bar
      const saved = await saveRecord();
      if (saved) {
        setGeneratingPdfs(true);
        setPdfProgress(0);
        
        // Start progress animation (10 seconds to reach ~90%)
        const startTime = Date.now();
        const duration = 10000; // 10 seconds
        progressIntervalRef.current = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min((elapsed / duration) * 90, 90);
          setPdfProgress(progress);
        }, 100);

        try {
          const docs = await generatePdfs(appointmentId, appointmentStartTime);
          // Clear interval and complete progress
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
          setPdfProgress(100);
          setGeneratedDocs(docs);
          
          // Small delay to show 100% before moving
          setTimeout(() => {
            setActiveStep(13);
            setGeneratingPdfs(false);
            setPdfProgress(0);
          }, 500);
        } catch (err) {
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
          setPdfProgress(0);
          setGeneratingPdfs(false);
          errorAlert("Erro ao gerar documentos PDF");
        }
      }
    } else if (activeStep === 13) {
      // Signature step - open SafeID in new tab
      const signatureUrl = startSignature(appointmentId);
      window.open(signatureUrl, "_blank");
      setSignatureComplete(true);
      setActiveStep(14);
    } else {
      setActiveStep((prev) => Math.min(prev + 1, STEP_LABELS.length - 1));
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleStepClick = (step: number) => {
    setActiveStep(step);
  };

  useEffect(() => {
    if (!open) return;

    stepButtonRefs.current[activeStep]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [activeStep, open]);

  const getCertificatePreview = () => {
    const formatTime = (value: string) =>
      new Date(value).toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "America/Sao_Paulo",
      });

    const startTime = appointmentStartTime ? formatTime(appointmentStartTime) : "__:__";
    const endTime = formatTime(new Date().toISOString());

    return `Atesto para os devidos fins que o(a) paciente ${patientName || "{nome do paciente}"} compareceu à consulta médica das ${startTime} até ${endTime}.\n\nO(a) paciente foi diagnosticado com ${cid || "{CID}"}, e recomendo:`;
  };

  const renderTextField = (
    label: string,
    value: string,
    setValue: (v: string) => void,
    multiline: boolean = true,
    rows: number = 4
  ) => (
    <TextField
      fullWidth
      label={label}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      multiline={multiline}
      rows={rows}
      variant="outlined"
      sx={{ mb: 2 }}
    />
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Complaint
        return renderTextField(
          "Queixa Principal",
          complaint,
          setComplaint
        );

      case 1: // History
        return renderTextField(
          "Histórico da Doença",
          history,
          setHistory
        );

      case 2: // Comorbidities
        return (
          <Box>
            <TextField
              fullWidth
              label="Buscar Comorbidades"
              value={comorbiditySearch}
              onChange={(e) => handleComorbiditySearchChange(e.target.value)}
              sx={{ mb: 2 }}
            />
            {loadingComorbidities && <CircularProgress size={20} />}
            {comorbiditySearch.length >= 2 && !loadingComorbidities && (
              <Paper sx={{ maxHeight: 200, overflow: "auto", mb: 2 }}>
                <List dense>
                  {comorbidityOptions.map((c) => (
                    <ListItem
                      key={c.id}
                      button
                      onClick={() => addComorbidity(c)}
                    >
                      <ListItemText primary={c.name} />
                    </ListItem>
                  ))}
                  {comorbidityOptions.length === 0 && (
                    <ListItem
                      button
                      onClick={async () => {
                        try {
                          const newComorbidity = await createComorbidity(comorbiditySearch);
                          addComorbidity(newComorbidity);
                          success("Comorbidade adicionada com sucesso!");
                        } catch (err) {
                          errorAlert("Erro ao criar comorbidade");
                        }
                      }}
                      sx={{ bgcolor: "#EFF6FF" }}
                    >
                      <Add sx={{ mr: 1, color: "#2563EB" }} />
                      <ListItemText
                        primary={`Adicionar "${comorbiditySearch}"`}
                        primaryTypographyProps={{ color: "#2563EB", fontWeight: 500 }}
                      />
                    </ListItem>
                  )}
                </List>
              </Paper>
            )}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Comorbidades Selecionadas:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {selectedComorbidities.map((c) => (
                <Chip
                  key={c.id}
                  label={c.name}
                  onDelete={() => removeComorbidity(c.id)}
                  color="primary"
                />
              ))}
            </Box>
          </Box>
        );

      case 3: // Current medicines
        return renderTextField(
          "Medicamentos em Uso",
          currentMedicines,
          setCurrentMedicines
        );

      case 4: // Exams done
        return renderTextField(
          "Exame Clínico",
          exams,
          setExams
        );

      case 5: // Hypothesis
        return renderTextField(
          "Hipótese Diagnóstica",
          hypothesis,
          setHypothesis
        );

      case 6: // Conduct
        return renderTextField(
          "Conduta Médica",
          conduct,
          setConduct
        );

      case 7: // Clinical exams requested
        return (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Exames de Imagem
            </Typography>
            {examsRequested.map((exam, index) => (
              <Box
                key={index}
                sx={{ display: "flex", alignItems: "center", mb: 1 }}
              >
                <TextField
                  fullWidth
                  label={`Exame ${index + 1}`}
                  value={exam}
                  onChange={(e) =>
                    updateExamField("clinical", index, e.target.value)
                  }
                />
                {examsRequested.length > 1 && (
                  <IconButton onClick={() => removeExamField("clinical", index)}>
                    <Delete />
                  </IconButton>
                )}
              </Box>
            ))}
            <Button
              startIcon={<Add />}
              onClick={() => addExamField("clinical")}
              sx={{ mt: 1 }}
            >
              Adicionar Exame
            </Button>
          </Box>
        );

      case 8: // Laboratory exams requested
        return (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Exames Laboratoriais
            </Typography>
            {laboratoryExamsRequested.map((exam, index) => (
              <Box
                key={index}
                sx={{ display: "flex", alignItems: "center", mb: 1 }}
              >
                <TextField
                  fullWidth
                  label={`Exame ${index + 1}`}
                  value={exam}
                  onChange={(e) =>
                    updateExamField("laboratory", index, e.target.value)
                  }
                />
                {laboratoryExamsRequested.length > 1 && (
                  <IconButton
                    onClick={() => removeExamField("laboratory", index)}
                  >
                    <Delete />
                  </IconButton>
                )}
              </Box>
            ))}
            <Button
              startIcon={<Add />}
              onClick={() => addExamField("laboratory")}
              sx={{ mt: 1 }}
            >
              Adicionar Exame
            </Button>
          </Box>
        );

      case 9: // Medicines prescribed
        return (
          <Box>
            <TextField
              fullWidth
              label="Buscar Medicamentos"
              inputRef={medicineSearchRef}
              value={medicineSearch}
              onChange={(e) => handleMedicineSearchChange(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Box
              ref={medicineListRef}
              onScroll={handleMedicineScroll}
              sx={{ maxHeight: 200, overflow: "auto", mb: 2 }}
            >
              {medicineSearch.length >= 2 && !loadingMedicines && (
                <Paper>
                  <List dense>
                    {medicineOptions.map((m) => (
                      <ListItem
                        key={m.id}
                        button
                        onClick={() => openDosageModal(m)}
                      >
                        <ListItemText primary={m.name} />
                      </ListItem>
                    ))}
                    {medicineOptions.length === 0 && (
                      <ListItem
                        button
                        onClick={async () => {
                          try {
                            const newMedicine = await createMedicine(medicineSearch);
                            openDosageModal(newMedicine);
                            success("Medicamento adicionado com sucesso!");
                          } catch (err) {
                            errorAlert("Erro ao criar medicamento");
                          }
                        }}
                        sx={{ bgcolor: "#EFF6FF" }}
                      >
                        <Add sx={{ mr: 1, color: "#2563EB" }} />
                        <ListItemText
                          primary={`Adicionar "${medicineSearch}"`}
                          primaryTypographyProps={{ color: "#2563EB", fontWeight: 500 }}
                        />
                      </ListItem>
                    )}
                  </List>
                </Paper>
              )}
              {loadingMedicines && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
            </Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Prescrever Medicamento
            </Typography>
            <List>
              {selectedMedicines.map((m) => (
                <ListItem key={m.medicine.id}>
                  <ListItemText
                    primary={m.medicine.name}
                    secondary={`Dosagem: ${m.dosage}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton onClick={() => removeMedicine(m.medicine.id)}>
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>

            {/* Dosage Modal */}
            <Dialog
              open={dosageModalOpen}
              onClose={() => setDosageModalOpen(false)}
              maxWidth={false}
              PaperProps={{
                sx: {
                  width: "90vw",
                  height: "80vh",
                  maxWidth: "90vw",
                  maxHeight: "80vh",
                  display: "flex",
                  flexDirection: "column",
                },
              }}
            >
              <DialogTitle>{currentMedicine?.name}</DialogTitle>
              <DialogContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <TextField
                  fullWidth
                  label="Dosagem"
                  value={currentDosage}
                  onChange={(e) => setCurrentDosage(e.target.value)}
                  sx={{ mt: 2 }}
                  placeholder="Ex: 1 comprimido de 8 em 8 horas"
                  multiline
                  rows={6}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDosageModalOpen(false)}>Cancelar</Button>
                <Button
                  variant="contained"
                  onClick={saveMedicineWithDosage}
                  disabled={!currentDosage}
                >
                  Adicionar
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        );

      case 10: // CID
        return (
          <TextField
            fullWidth
            label="CID (opcional)"
            value={cid}
            onChange={(e) => setCid(e.target.value)}
            placeholder="Ex: J06.9"
          />
        );

      case 11: // Certificate
        return (
          <Box>
            <Paper sx={{ p: 2, mb: 2, bgcolor: "#f5f5f5" }}>
              <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                {getCertificatePreview()}
              </Typography>
            </Paper>
            <TextField
              fullWidth
              label="Texto personalizado do atestado"
              value={healthCertificate}
              onChange={(e) => setHealthCertificate(e.target.value)}
              multiline
              rows={4}
              placeholder="Escreva aqui as recomendações..."
            />
          </Box>
        );

      case 12: // Report
        return renderTextField(
          "Resumo Final (opcional)",
          report,
          setReport,
          true,
          6
        );

      case 13: // Signature - show documents with tabs
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
              Assinatura Eletrônica
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: "center" }}>
              Revise os documentos abaixo e clique em "Assinar Documentos" para
              abrir o SafeID em uma nova aba.
            </Typography>
            {generatedDocs.length === 0 ? (
              <Typography sx={{ textAlign: "center", py: 4 }}>
                Nenhum documento gerado.
              </Typography>
            ) : (
              <>
                <Tabs
                  value={selectedDocTab}
                  onChange={(_, v) => setSelectedDocTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
                >
                  {generatedDocs.map((doc, index) => (
                    <Tab
                      key={index}
                      ref={(el) => {
                        docTabRefs.current[index] = el;
                      }}
                      label={
                        doc.type === "clinical_exams"
                          ? "Exames Imagem"
                          : doc.type === "laboratory_exams"
                            ? "Exames Lab."
                            : doc.type === "prescription"
                              ? "Receita"
                              : doc.type === "certificate"
                                ? "Atestado"
                                : "Relatório"
                      }
                    />
                  ))}
                </Tabs>
                <Box sx={{ height: 450, border: "1px solid #E2E8F0", borderRadius: 1 }}>
                  {generatedDocs[selectedDocTab] && (
                    <iframe
                      src={generatedDocs[selectedDocTab].url}
                      width="100%"
                      height="100%"
                      style={{ border: "none" }}
                      title="PDF Preview"
                    />
                  )}
                </Box>
              </>
            )}
            {signatureComplete && (
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Chip
                  icon={<CheckCircle />}
                  label="Assinatura iniciada"
                  color="success"
                />
              </Box>
            )}
          </Box>
        );

      case 14: // Documents - final view
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
              Documentos Assinados
            </Typography>
            {generatedDocs.length === 0 ? (
              <Typography sx={{ textAlign: "center", py: 4 }}>
                Nenhum documento gerado.
              </Typography>
            ) : (
              <>
                <Tabs
                  value={selectedDocTab}
                  onChange={(_, v) => setSelectedDocTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
                >
                  {generatedDocs.map((doc, index) => (
                    <Tab
                      key={index}
                      ref={(el) => {
                        docTabRefs.current[index] = el;
                      }}
                      label={
                        doc.type === "clinical_exams"
                          ? "Exames Imagem"
                          : doc.type === "laboratory_exams"
                            ? "Exames Lab."
                            : doc.type === "prescription"
                              ? "Receita"
                              : doc.type === "certificate"
                                ? "Atestado"
                                : "Relatório"
                      }
                    />
                  ))}
                </Tabs>
                <Box sx={{ height: 500, border: "1px solid #E2E8F0", borderRadius: 1 }}>
                  {generatedDocs[selectedDocTab] && (
                    <iframe
                      src={generatedDocs[selectedDocTab].url}
                      width="100%"
                      height="100%"
                      style={{ border: "none" }}
                      title="PDF Preview"
                    />
                  )}
                </Box>
              </>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: "80vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #E2E8F0",
          pb: 2,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Prontuário Eletrônico
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <Box
        sx={{
          px: { xs: 1, sm: 3 },
          pt: { xs: 2, sm: 3 },
          pb: { xs: 0, sm: 1 },
          minHeight: { xs: "auto", sm: 120 },
        }}
      >
        {/* Desktop stepper */}
        <Box
          sx={{
            display: { xs: "none", sm: "block" },
            overflowX: "auto",
            overflowY: "hidden",
            pb: 2,
            "&::-webkit-scrollbar": {
              height: 4,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#CBD5E1",
              borderRadius: 2,
            },
          }}
        >
          <Stepper
            nonLinear
            activeStep={activeStep}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              width: "fit-content",
              minWidth: "100%",
              "& .MuiStep-root": {
                flex: "0 0 auto",
              },
              "& .MuiStepButton-root": {
                alignItems: "flex-start",
              },
              "& .MuiStepLabel-iconContainer": {
                display: "none",
              },
            }}
          >
            {STEP_LABELS.map((label, index) => (
              <Step key={label}>
                <StepButton
                  ref={(el) => {
                    stepButtonRefs.current[index] = el;
                  }}
                  onClick={() => handleStepClick(index)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      gap: 0.5,
                      minWidth: 88,
                      px: 0.75,
                      pt: 0.5,
                      pb: 0,
                      ml: index === 0 ? 1 : 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor:
                          index === activeStep
                            ? PRIMARY_STEP_COLOR
                            : index < activeStep
                            ? "#10B981"
                            : "#E2E8F0",
                        color: index <= activeStep ? "#fff" : "#64748B",
                        fontSize: 13,
                        fontWeight: 700,
                        boxShadow:
                          index === activeStep
                            ? "0 0 0 3px rgba(10,97,114,0.18)"
                            : "none",
                        flexShrink: 0,
                      }}
                    >
                      {index < activeStep ? (
                        <CheckCircle sx={{ fontSize: 18, color: "#fff" }} />
                      ) : (
                        index + 1
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        lineHeight: 1.05,
                        textTransform: "none",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                        textAlign: "center",
                        maxWidth: 88,
                        minHeight: 28,
                        color: index <= activeStep ? "#0F172A" : "#64748B",
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Mobile windowed step indicator */}
        {(() => {
          const PREVIOUS_VISIBLE = 2;
          const NEXT_VISIBLE = 1;
          const total = STEP_LABELS.length;
          const wStart = Math.max(0, activeStep - PREVIOUS_VISIBLE);
          const wEnd = Math.min(total - 1, activeStep + NEXT_VISIBLE);
          const visibleIndices = Array.from({ length: wEnd - wStart + 1 }, (_, i) => wStart + i);
          return (
            <Box
              sx={{
                display: { xs: "flex", sm: "none" },
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                minHeight: 64,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  overflowX: "auto",
                  maxWidth: "100%",
                  pb: 1.25,
                }}
              >
                {wStart > 0 && (
                  <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 700, lineHeight: 1 }}>...</Typography>
                )}
                {visibleIndices.map((idx) => (
                  <Box
                    key={idx}
                    onClick={() => handleStepClick(idx)}
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 999,
                      cursor: "pointer",
                      flexShrink: 0,
                      backgroundColor:
                        idx === activeStep
                          ? PRIMARY_STEP_COLOR
                          : idx < activeStep
                          ? "#10B981"
                          : "#E2E8F0",
                      color: idx <= activeStep ? "#fff" : "#64748B",
                      maxWidth: 160,
                      textAlign: "center",
                      fontWeight: 700,
                      boxShadow: idx === activeStep ? "0 0 0 3px rgba(10,97,114,0.2)" : "none",
                      transition: "all 0.2s",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: idx <= activeStep ? "#fff" : "#64748B",
                        fontWeight: 700,
                        lineHeight: 1.15,
                        display: "block",
                        whiteSpace: "normal",
                      }}
                    >
                      {idx < activeStep ? (
                        <CheckCircle sx={{ fontSize: 18, color: "#fff", display: "block", mx: "auto" }} />
                      ) : (
                        STEP_LABELS[idx]
                      )}
                    </Typography>
                  </Box>
                ))}
                {wEnd < total - 1 && (
                  <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 700, lineHeight: 1 }}>...</Typography>
                )}
              </Box>
            </Box>
          );
        })()}

        {!HIDE_CENTER_STEP_LABELS.has(activeStep) && (
          <Typography
            variant="subtitle1"
            sx={{ textAlign: "center", mt: 1, color: "#0A6172", fontWeight: 600 }}
          >
            {STEP_LABELS[activeStep]}
          </Typography>
        )}
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 300,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          renderStepContent()
        )}
      </DialogContent>

      <DialogActions
        sx={{ p: 2, borderTop: "1px solid #E2E8F0", justifyContent: "space-between" }}
      >
        <Button onClick={handleBack} disabled={activeStep === 0}>
          Voltar
        </Button>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {activeStep < 13 && (
            <Button
              onClick={saveRecord}
              disabled={saving}
              sx={{ mr: 1 }}
            >
              {saving ? <CircularProgress size={20} /> : "Salvar"}
            </Button>
          )}
          {generatingPdfs && (
            <Box sx={{ width: 200, mr: 2, display: "inline-block" }}>
              <LinearProgress
                variant="determinate"
                value={pdfProgress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "#E2E8F0",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 4,
                    backgroundColor: "#2563EB",
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{ display: "block", textAlign: "center", mt: 0.5 }}
              >
                {Math.round(pdfProgress)}%
              </Typography>
            </Box>
          )}
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              activeStep === STEP_LABELS.length - 1 ||
              generatingPdfs ||
              saving
            }
          >
            {generatingPdfs ? (
              "Gerando..."
            ) : activeStep === 12 ? (
              "Gerar Documentos"
            ) : activeStep === 13 ? (
              "Assinar Documentos"
            ) : (
              "Avançar"
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default MedicalRecordModal;
